import React from 'react'
import { Currency, CurrencyAmount } from '@pancakeswap-libs/sdk'
import { Button, Text } from '@pancakeswap-libs/uikit'
import styled from 'styled-components'
import { AggregatedQuoteResultBest } from '../../aggregator'
import TransactionConfirmationModal, { ConfirmationModalContent } from '../TransactionConfirmationModal'
import { AutoColumn } from '../Column'
import { RowBetween, RowFixed } from '../Row'
import CurrencyLogo from '../CurrencyLogo'
import AggregatorRoute from './AggregatorRoute'
import { severityFromBps } from '../../utils/aggregatorPrices'

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderColor ?? theme.colors.tertiary};
  margin: 4px 0;
`

const Warning = styled.div<{ severity: number }>`
  padding: 10px 12px;
  border-radius: 12px;
  background: ${({ theme, severity }) => (severity > 2 ? theme.colors.failure : theme.colors.tertiary)};
  color: ${({ theme, severity }) => (severity > 2 ? theme.colors.contrast : theme.colors.text)};
`

interface Props {
  isOpen: boolean
  onDismiss: () => void
  onConfirm: () => void
  attemptingTxn: boolean
  txHash: string | undefined
  quote: AggregatedQuoteResultBest | null
  inputAmount: CurrencyAmount | undefined
  outputAmount: CurrencyAmount | undefined
  minimumReceived: CurrencyAmount | undefined
  inputCurrency: Currency | undefined
  outputCurrency: Currency | undefined
  /** Quote refreshed while the modal was open and the user has not accepted it yet. */
  priceUpdated: boolean
  onAcceptPrice: () => void
}

/**
 * Confirmation for an aggregator-routed swap.
 *
 * The on-chain router path has its own modal built around the SDK's Trade object,
 * which an aggregator quote is not. Rather than force one shape onto the other, this
 * shows what an aggregator trade actually commits the user to: the guaranteed minimum,
 * the route, the price impact, and any fee.
 */
export default function AggregatorConfirmModal({
  isOpen,
  onDismiss,
  onConfirm,
  attemptingTxn,
  txHash,
  quote,
  inputAmount,
  outputAmount,
  minimumReceived,
  inputCurrency,
  outputCurrency,
  priceUpdated,
  onAcceptPrice,
}: Props) {
  const severity = severityFromBps(quote?.priceImpactBps)

  const topContent = () => (
    <AutoColumn gap="12px">
      <RowBetween align="center">
        <Text fontSize="22px">{inputAmount?.toSignificant(6) ?? '-'}</Text>
        <RowFixed gap="6px">
          <CurrencyLogo currency={inputCurrency} size="24px" />
          <Text fontSize="22px" ml="8px">
            {inputCurrency?.symbol}
          </Text>
        </RowFixed>
      </RowBetween>

      <RowBetween align="center">
        <Text fontSize="22px">{outputAmount?.toSignificant(6) ?? '-'}</Text>
        <RowFixed gap="6px">
          <CurrencyLogo currency={outputCurrency} size="24px" />
          <Text fontSize="22px" ml="8px">
            {outputCurrency?.symbol}
          </Text>
        </RowFixed>
      </RowBetween>

      {priceUpdated && (
        <Warning severity={2}>
          <RowBetween>
            <Text fontSize="14px" bold>
              Price updated
            </Text>
            <Button scale="sm" onClick={onAcceptPrice}>
              Accept
            </Button>
          </RowBetween>
        </Warning>
      )}
    </AutoColumn>
  )

  const bottomContent = () => (
    <AutoColumn gap="10px">
      <Divider />

      <RowBetween>
        <Text fontSize="14px" color="textSubtle">
          Minimum received
        </Text>
        <Text fontSize="14px">
          {minimumReceived?.toSignificant(6) ?? '-'} {outputCurrency?.symbol}
        </Text>
      </RowBetween>

      {quote?.priceImpactBps !== undefined && (
        <RowBetween>
          <Text fontSize="14px" color="textSubtle">
            Price impact
          </Text>
          <Text fontSize="14px" color={severity > 2 ? 'failure' : severity > 1 ? 'warning' : 'text'}>
            {(Math.abs(quote.priceImpactBps) / 100).toFixed(2)}%
          </Text>
        </RowBetween>
      )}

      {quote?.fee && (
        <RowBetween>
          <Text fontSize="14px" color="textSubtle">
            Service fee
          </Text>
          <Text fontSize="14px">{(quote.fee.bps / 100).toFixed(2)}%</Text>
        </RowBetween>
      )}

      <AggregatorRoute
        route={quote?.route ?? []}
        providerLabel={quote?.providerLabel}
        improvementBps={quote?.improvementBps}
      />

      {severity > 2 && (
        <Warning severity={severity}>
          <Text fontSize="13px" color="contrast">
            High price impact. You will lose a significant part of this trade to slippage.
          </Text>
        </Warning>
      )}

      <Button
        onClick={onConfirm}
        disabled={priceUpdated}
        width="100%"
        mt="8px"
        variant={severity > 2 ? 'danger' : 'primary'}
      >
        {priceUpdated ? 'Accept the new price first' : 'Confirm swap'}
      </Button>
    </AutoColumn>
  )

  return (
    <TransactionConfirmationModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      currencyToAdd={outputCurrency}
      pendingText={`Swapping ${inputAmount?.toSignificant(6) ?? ''} ${inputCurrency?.symbol ?? ''} for ${
        outputAmount?.toSignificant(6) ?? ''
      } ${outputCurrency?.symbol ?? ''}`}
      content={() => (
        <ConfirmationModalContent
          title="Confirm swap"
          onDismiss={onDismiss}
          topContent={topContent}
          bottomContent={bottomContent}
        />
      )}
    />
  )
}
